import React, { useState, useEffect, useCallback } from 'react';
import { talukaAPI } from '../../services/api';
import '../../components/taluka/TalukaLayout.css';

function BarChart({ data, labelKey, valueKey, color = '#1565c0' }) {
  const max = Math.max(...data.map(d => Number(d[valueKey]) || 0), 1);
  return (
    <div>
      {data.map((d, i) => (
        <div key={i} className="tk-bar-row">
          <span className="tk-bar-label" title={d[labelKey]}>
            {String(d[labelKey] || '').length > 14 ? String(d[labelKey]).slice(0, 13) + '…' : d[labelKey]}
          </span>
          <div className="tk-bar-track">
            <div className="tk-bar-fill" style={{ width: `${(Number(d[valueKey]) / max) * 100}%`, background: color }} />
          </div>
          <span className="tk-bar-value">{Number(d[valueKey]).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function statusBadge(status) {
  return <span className={`tk-badge tk-badge-${status}`}>{status?.replace('_', ' ')}</span>;
}

// Build { gp_name -> count } from [{tenant_id, count}, ...] and gp lookup
function buildGPCounts(rawArr, gps, sumField = 'count') {
  const gpMap = {};
  gps.forEach(g => { gpMap[g.id] = g.name; });
  const totals = {};
  rawArr.forEach(r => {
    const name = gpMap[r.tenant_id] || r.tenant_id;
    totals[name] = (totals[name] || 0) + Number(r[sumField] || r.count || 0);
  });
  return Object.entries(totals).map(([gp_name, count]) => ({ gp_name, count }));
}

// Total complaint counts by status across all GPs
function buildStatusCounts(complaintsByGP) {
  const map = {};
  complaintsByGP.forEach(r => {
    map[r.status] = (map[r.status] || 0) + Number(r.count || 0);
  });
  return Object.entries(map).map(([status, count]) => ({ status, count }));
}

export default function TalukaDashboard() {
  const [raw, setRaw]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await talukaAPI.getDashboard();
      setRaw(res.data?.data || res.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}>Loading dashboard…</div>;
  if (error)   return <div style={{ textAlign: 'center', padding: 60, color: '#c62828' }}>{error}</div>;

  const s               = raw?.summary           || {};
  const gps             = raw?.grampanchayats     || [];
  const rawComplaintsByGP = raw?.complaintsByGP   || [];
  const rawCitizensByGP   = raw?.citizensByGP     || [];
  const recentComplaints = raw?.recentComplaints  || [];
  const charts          = raw?.charts             || {};

  // Derived
  const byStatus      = buildStatusCounts(rawComplaintsByGP);
  const complaintsByGP = buildGPCounts(rawComplaintsByGP, gps);
  const citizensByGP   = buildGPCounts(rawCitizensByGP, gps);
  const byCategory    = (charts.complaintsByCategory || []).map(r => ({ category: r.category, count: Number(r.count) }));
  const byGender      = (charts.genderBreakdown || []).map(r => ({ gender: r.gender, count: Number(r.count) }));
  const byAge         = (charts.ageGroups || []).map(r => ({ age_group: r.age_group, count: Number(r.count) }));

  // Build gp name lookup for recent complaints
  const gpNameById = {};
  gps.forEach(g => { gpNameById[g.id] = g.name; });

  return (
    <div>
      <div className="tk-page-header">
        <h1>Taluka Overview Dashboard</h1>
        <p>Aggregated data across all Grampanchayats in your taluka</p>
      </div>

      {/* Summary stats */}
      <div className="tk-stat-grid">
        <div className="tk-stat-card">
          <div className="tk-stat-num">{s.totalGPs ?? 0}</div>
          <div className="tk-stat-label">Grampanchayats</div>
        </div>
        <div className="tk-stat-card green">
          <div className="tk-stat-num">{(s.totalCitizens ?? 0).toLocaleString()}</div>
          <div className="tk-stat-label">Total Citizens</div>
        </div>
        <div className="tk-stat-card purple">
          <div className="tk-stat-num">{(s.totalHouseholds ?? 0).toLocaleString()}</div>
          <div className="tk-stat-label">Households</div>
        </div>
        <div className="tk-stat-card orange">
          <div className="tk-stat-num">{(s.totalComplaints ?? 0).toLocaleString()}</div>
          <div className="tk-stat-label">Total Complaints</div>
        </div>
        <div className="tk-stat-card red">
          <div className="tk-stat-num">{(s.openComplaints ?? 0).toLocaleString()}</div>
          <div className="tk-stat-label">Open Complaints</div>
        </div>
        <div className="tk-stat-card teal">
          <div className="tk-stat-num">{(s.resolvedComplaints ?? 0).toLocaleString()}</div>
          <div className="tk-stat-label">Resolved</div>
        </div>
      </div>

      {/* Charts row */}
      <div className="tk-charts-grid">
        {byStatus.length > 0 && (
          <div className="tk-chart-card">
            <h4>Complaints by Status</h4>
            <BarChart data={byStatus} labelKey="status" valueKey="count" color="#1565c0" />
          </div>
        )}
        {byCategory.length > 0 && (
          <div className="tk-chart-card">
            <h4>Complaints by Category</h4>
            <BarChart data={byCategory} labelKey="category" valueKey="count" color="#e65100" />
          </div>
        )}
        {complaintsByGP.length > 0 && (
          <div className="tk-chart-card">
            <h4>Complaints per Grampanchayat</h4>
            <BarChart data={complaintsByGP} labelKey="gp_name" valueKey="count" color="#6a1b9a" />
          </div>
        )}
        {citizensByGP.length > 0 && (
          <div className="tk-chart-card">
            <h4>Citizens per Grampanchayat</h4>
            <BarChart data={citizensByGP} labelKey="gp_name" valueKey="count" color="#2e7d32" />
          </div>
        )}
        {byGender.length > 0 && (
          <div className="tk-chart-card">
            <h4>Citizens by Gender</h4>
            <BarChart data={byGender} labelKey="gender" valueKey="count" color="#00695c" />
          </div>
        )}
        {byAge.length > 0 && (
          <div className="tk-chart-card">
            <h4>Citizens by Age Group</h4>
            <BarChart data={byAge} labelKey="age_group" valueKey="count" color="#ad1457" />
          </div>
        )}
      </div>

      {/* Recent complaints */}
      {recentComplaints.length > 0 && (
        <div className="tk-card">
          <h3>Recent Complaints</h3>
          <div className="tk-table-wrap">
            <table className="tk-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>GP</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentComplaints.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', color: '#1565c0' }}>{c.complaint_uid || c.id}</td>
                    <td>{c.title}</td>
                    <td style={{ textTransform: 'capitalize' }}>{c.category}</td>
                    <td>{gpNameById[c.tenant_id] || '—'}</td>
                    <td>{statusBadge(c.status)}</td>
                    <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
